import { DEFAULT_AR_CRASH_ISOLATION, validateArCrashIsolationOptions } from "./ar/ar-crash-isolation.js";
import { r as createLogger } from "./logger-BU8YqMGU.js";
import { CameraBlitCapture, computeAspectFitSize, computeCaptureSize } from "./ar/camera-blit-capture.js";
import { applyChromiumProjectionLayerWorkaround } from "./ar/chromium-camera-access-workaround.js";
import { DepthSampler, wrapXRDepthInfo } from "./ar/depth-sampler.js";
import { clearFrameUpdates, runFrameUpdates } from "./ar/frame-loop.js";
import { clearXrFrameUpdates, runXrFrameUpdates } from "./ar/xr-frame-loop.js";
import { probeImmersiveArSupport } from "./ar/webxr-support-probe.js";
import { createSceneHierarchy } from "./ar/ar-scene-hierarchy.js";
import { n as ImageCaptureManager, t as DEFAULT_CAPTURE_CONFIG } from "./image-capture-Bx9LA5_p.js";
import { clearLastRestartedPayload, originReset, poseLost, poseReceived, resetTracking, selectLastRestartedPayload, selectTrackingPhase } from "./state/tracking-slice.js";
import { acquireCameraTexture } from "./ar/xr-camera-texture.js";
import { n as runSessionDisposers } from "./session-disposers-I5HJZq-T.js";
import { t as getLastDeviceOrientation } from "./device-orientation-cache-Cmn72tA0.js";
import { createCss3dRendererManager } from "./visualization/css3d-renderer-manager.js";
import * as THREE from "three";
//#region ../src/ar/camera-frame-source.ts
const DEFAULT_CONFIG = { intervalMs: 125 };
/**
* Throttled RGBA capturer. Construct with the injected `capture`/`onCapture`
* pair, `start()`, then call `onFrame(timestamp)` once per XR frame.
*/
var CameraFrameSource = class {
	callbacks;
	config;
	running = false;
	captureCount = 0;
	lastCaptureTime = -Infinity;
	constructor(callbacks, config) {
		this.callbacks = callbacks;
		this.config = { ...DEFAULT_CONFIG };
		if (config) this.updateConfig(config);
	}
	/** Begin throttling. Resets the cadence so the first tick captures. */
	start() {
		this.running = true;
		this.captureCount = 0;
		this.lastCaptureTime = -Infinity;
	}
	/** Stop capturing. `onFrame` becomes a no-op until `start()` is called again. */
	stop() {
		this.running = false;
	}
	isRunning() {
		return this.running;
	}
	/** Number of frames successfully captured + delivered since `start()`. */
	getFrameCount() {
		return this.captureCount;
	}
	getConfig() {
		return { ...this.config };
	}
	/**
	* Apply partial config (e.g. the app's detection cadence). Invalid values
	* are ignored defensively — `intervalMs` requires a finite positive number.
	*/
	updateConfig(config) {
		if (typeof config.intervalMs === "number" && Number.isFinite(config.intervalMs) && config.intervalMs > 0) this.config.intervalMs = config.intervalMs;
	}
	/**
	* Per-XR-frame tick. Captures + delivers a frame at most once per
	* `intervalMs`; otherwise a cheap no-op so it is safe to call every frame.
	*
	* @param timestamp - monotonic frame time in ms (the XR `time` argument).
	*/
	onFrame(timestamp) {
		if (!this.running) return;
		if (timestamp - this.lastCaptureTime < this.config.intervalMs) return;
		const image = this.captureSafely();
		if (!image) return;
		this.lastCaptureTime = timestamp;
		this.captureCount++;
		this.callbacks.onCapture(image);
	}
	/**
	* Run the injected capture, guarded so a blit failure (e.g. GL context loss)
	* can never throw out of the XR frame loop — it degrades to "no frame this
	* tick", exactly like a missing texture.
	*/
	captureSafely() {
		try {
			return this.callbacks.capture();
		} catch {
			return null;
		}
	}
};
//#endregion
//#region ../src/ar/depth-rgb-lookup.ts
/**
* Build an {@link RgbLookup} over an RGBA readback buffer.
*
* Defensive boundary: returns `null` (no lookup) when the dimensions are
* not positive integers or the buffer length does not match
* `width × height × 4` — callers then simply emit color-less points
* rather than crashing the capture path.
*/
function createRgbLookup(pixels, width, height) {
	if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0 || pixels.length !== width * height * 4) return null;
	return (screenX, screenY) => {
		if (!Number.isFinite(screenX) || !Number.isFinite(screenY) || screenX < 0 || screenX > 1 || screenY < 0 || screenY > 1) return null;
		const col = Math.min(width - 1, Math.floor(screenX * width));
		const rowFromTop = Math.min(height - 1, Math.floor(screenY * height));
		const i = ((height - 1 - rowFromTop) * width + col) * 4;
		return [
			pixels[i],
			pixels[i + 1],
			pixels[i + 2]
		];
	};
}
//#endregion
//#region ../src/ar/webxr-session.ts
/**
* WebXR Session Module
*
* Handles WebXR AR session initialization and frame loop.
*
* ARCHITECTURE NOTE: See docs/architecture-ar-gps-pose-separation.md
*
* Scene Hierarchy:
*   scene (GPS world frame — NUE: X=North, Y=Up, Z=East)
*   └── arWorldGroup (local space = NUE; receives alignment matrix)
*       └── basisChangeNode ('webxr-to-nue', constant WEBXR_TO_NUE matrix)
*           └── arpose (local space = WebXR)
*               └── camera (raw AR pose from WebXR)
*
* The arWorldGroup is transformed by the GpsPlusSlamJs alignment matrix.
* The camera's LOCAL transform is the raw AR pose from WebXR.
* The camera's WORLD transform is the GPS-aligned world pose.
*/
const log = createLogger("WebXR");
/** Runtime guard for `XRView.camera` candidates (finite, positive dimensions). */
function isXRCameraLike(value) {
	if (typeof value !== "object" || value === null) return false;
	const camera = value;
	return typeof camera.width === "number" && Number.isFinite(camera.width) && camera.width > 0 && typeof camera.height === "number" && Number.isFinite(camera.height) && camera.height > 0;
}
/**
* Extract a validated XRCameraLike from an XRViewerPose.
*
* Returns null in every case where the per-frame texture acquisition must be
* skipped — i.e. whenever the caller should treat any previously cached
* texture as stale and clear it. Centralising the preconditions here makes
* it impossible to accidentally fall through without clearing the cache.
*
* Cases that return null:
*   - pose is null (tracking lost)
*   - pose has no views
*   - the first view has no `camera` property (camera-access not granted)
*   - the camera property is not a valid XRCameraLike (zero/NaN dimensions)
*
* @see gps-plus-slam/GpsPlusSlamJs_Docs/docs/2026-02-06-bug-camera-frames-black.md
*/
function getXrCameraFromPose(pose) {
	if (!pose) return null;
	const view = pose.views[0];
	if (!view) return null;
	const candidate = view.camera;
	return isXRCameraLike(candidate) ? candidate : null;
}
/**
* Decide whether the one-time camera-access grant diagnostic should fire
* on the current XR frame.
*
* The diagnostic is only meaningful when we actually have a valid pose:
* if `pose` is null (e.g. session starts with tracking lost), then the
* derived `xrCamera` is null regardless of permission state, and logging
* "NOT GRANTED" would be a false negative that permanently suppresses the
* correct status because `cameraAccessLoggedOnce` would latch to true.
*
* Additional conditions:
*   - `alreadyLogged` prevents spamming the log every frame.
*   - `captureActive` ensures we only care when an image capture session
*     is running (otherwise the diagnostic has no audience).
*
* This predicate is extracted as a pure function so the guard against the
* "first frame has no pose" false-negative regression is directly testable.
*/
function shouldLogCameraAccessDiagnostic(pose, alreadyLogged, captureActive) {
	return pose !== null && !alreadyLogged && captureActive;
}
/**
* Extract the reset transform from an XRReferenceSpaceEvent-like object.
*
* Distinguishes three cases per OdometryTrackingRestartedPayload semantics:
* - Transform property missing (older browsers) → returns `undefined`
* - Transform property present but null (runtime can't determine delta) → returns `null`
* - Transform property present with data → returns `ResetTransformData`
*
* This is a pure function extracted for testability.
*
* @param event - The event object, cast to a record with an optional transform property
* @returns ResetTransformData, null, or undefined
*/
function extractResetTransformData(event) {
	if (!("transform" in event)) return;
	const transform = event.transform;
	if (!transform) return null;
	const pos = transform.position;
	const ori = transform.orientation;
	return {
		position: [
			pos.x,
			pos.y,
			pos.z
		],
		orientation: [
			ori.x,
			ori.y,
			ori.z,
			ori.w
		]
	};
}
/**
* Extract pose data from an XRViewerPose.
* Returns null if pose or views are unavailable.
*
* This is a pure function extracted for testability.
*
* @param pose - The XRViewerPose from frame.getViewerPose()
* @returns ARPose with position and orientation, or null
*/
function extractPoseFromViewer(pose) {
	if (!pose) return null;
	const view = pose.views[0];
	if (!view) return null;
	const { position, orientation } = view.transform;
	return {
		position: {
			x: position.x,
			y: position.y,
			z: position.z
		},
		orientation: {
			x: orientation.x,
			y: orientation.y,
			z: orientation.z,
			w: orientation.w
		}
	};
}
/**
* Default longer-edge resolution (px) for the camera-frame blit the QR / CV
* detector sees. The on-device capture-resolution sweep (2026-06-17, via the
* `?capture=` override) showed **1024** decodes a small / out-of-focus QR
* markedly better than the prior 512 with no perceptible cadence cost on the
* test phone; 2048 helped slightly more but risks low-end devices (4096 lagged),
* so 1024 is the safe default. Raise per-consumer via
* `startCameraFrameCapture({ captureSize })`.
*
* (Declared above the handle factory because the factory runs at module
* evaluation — a later `const` would still be in its temporal dead zone.)
*
* @see GpsPlusSlamJs_Docs/docs/2026-06-17-1020-qr-size-accuracy-and-thin-demo-plan.md (WS-C)
*/
const DEFAULT_CAMERA_FRAME_CAPTURE_SIZE = 1024;
/**
* Normalize an optional callback to the handle's explicit-`null` convention
* (also keeps `createArSessionHandle` under the lint complexity cap — each
* inline `?? null` would count as a decision point).
*/
function orNull(value) {
	return value ?? null;
}
function createImageCaptureCluster(cb) {
	return {
		manager: null,
		blit: null,
		onCaptured: orNull(cb?.onCaptured),
		getScreenRotation: orNull(cb?.getScreenRotation),
		onFailed: orNull(cb?.onFailed),
		onSuspicious: orNull(cb?.onSuspicious),
		qualityAnalyzer: orNull(cb?.qualityAnalyzer)
	};
}
function createDepthCluster(cb) {
	return {
		sampler: null,
		rgbBlit: null,
		onCaptured: orNull(cb?.onCaptured),
		onUnavailable: orNull(cb?.onUnavailable)
	};
}
function createCameraFrameCluster(cb) {
	return {
		source: null,
		blit: null,
		captureSize: DEFAULT_CAMERA_FRAME_CAPTURE_SIZE,
		onFrame: orNull(cb?.onFrame)
	};
}
function createTrackingCluster(cb) {
	return {
		store: orNull(cb?.store),
		phaseUnsubscribe: null,
		onRestarted: orNull(cb?.onRestarted),
		onLost: orNull(cb?.onLost),
		onRecovered: orNull(cb?.onRecovered)
	};
}
function createSceneGraphCluster() {
	return {
		renderer: null,
		scene: null,
		camera: null,
		arWorldGroup: null,
		css3d: null
	};
}
function createArSessionHandle(crashIsolation, callbacks) {
	return {
		sceneGraph: createSceneGraphCluster(),
		xrSession: null,
		latestArPose: null,
		onFrame: orNull(callbacks.onFrame),
		imageCapture: createImageCaptureCluster(callbacks.imageCapture),
		depth: createDepthCluster(callbacks.depth),
		cameraFrame: createCameraFrameCluster(callbacks.cameraFrame),
		tracking: createTrackingCluster(callbacks.tracking),
		crashIsolation,
		onSessionEnd: orNull(callbacks.onSessionEnd),
		endRequestedByApp: false,
		lastFrameTime: 0,
		latestCameraTexture: null,
		latestCameraWidth: 0,
		latestCameraHeight: 0,
		cameraAccessLoggedOnce: false,
		getCameraTextureNullCount: 0
	};
}
/** Fresh default handle for the pre-init / post-teardown state. */
function defaultArSessionHandle() {
	return createArSessionHandle({ ...DEFAULT_AR_CRASH_ISOLATION }, {});
}
/**
* The live session handle — the module's ONLY mutable since Stage 3. Always
* non-null: teardown REPLACES it with a fresh default handle, so
* pre-init/post-teardown reads need no null checks. (The plan sketched making
* it nullable at Stage 3; the always-non-null default-handle pattern proved
* strictly simpler — same reset guarantee, zero null-guard churn at the ~40
* read sites.)
*/
let activeSession = defaultArSessionHandle();
/**
* Reset WebXR module state - exported for testing only.
* @internal
*/
function resetWebXRState() {
	const { renderer } = activeSession.sceneGraph;
	if (renderer) {
		renderer.setAnimationLoop(null);
		if (renderer.domElement.parentElement) renderer.domElement.parentElement.removeChild(renderer.domElement);
		renderer.dispose();
	}
	clearFrameUpdates();
	clearXrFrameUpdates();
	runSessionDisposers();
	activeSession.tracking.phaseUnsubscribe?.();
	cleanupBlitResources();
	activeSession.depth.rgbBlit?.dispose();
	activeSession.cameraFrame.blit?.dispose();
	activeSession.sceneGraph.css3d?.dispose();
	activeSession = defaultArSessionHandle();
}
/** Readback size for the depth-RGB blit (plan §5: "e.g. 256×192 suffices"). */
const DEPTH_RGB_BLIT_CONFIG = {
	width: 256,
	height: 192
};
const GET_CAMERA_TEXTURE_LOG_THRESHOLD = 5;
/**
* Dispose the JPEG blit pipeline and clear the cached camera texture.
* Shared by resetWebXRState() and stopImageCapture() to avoid duplication.
*/
function cleanupBlitResources() {
	const { imageCapture } = activeSession;
	if (imageCapture.blit) {
		imageCapture.blit.dispose();
		imageCapture.blit = null;
	}
	activeSession.latestCameraTexture = null;
}
/**
* Acquire a camera-color lookup for the current XR frame (passed to the
* DepthSampler as `acquireRgbLookup`; called at most once per emitted
* sample). Returns null — color-less points — when camera access or the
* readback is unavailable; the blit instance lazily (re)creates itself so
* a disposal elsewhere is self-healing.
*/
function acquireDepthRgbLookup() {
	const { latestCameraTexture, depth } = activeSession;
	const { renderer } = activeSession.sceneGraph;
	if (!renderer || !latestCameraTexture) return null;
	depth.rgbBlit ??= new CameraBlitCapture(DEPTH_RGB_BLIT_CONFIG);
	const readback = depth.rgbBlit.captureToPixels(renderer, latestCameraTexture);
	return readback ? createRgbLookup(readback.pixels, readback.width, readback.height) : null;
}
/**
* Capture the current XR frame as top-left RGBA for CV detection (the
* `capture` injected into {@link CameraFrameSource}; called at most once per
* detection interval). Returns null — no frame this tick — when camera access
* or the texture is unavailable; the lazy blit makes a disposal elsewhere
* self-healing. Reuses `latestCameraTexture`, exactly like the depth-RGB path.
*/
function acquireCameraFrameRgba() {
	const { latestCameraTexture, cameraFrame } = activeSession;
	const { renderer } = activeSession.sceneGraph;
	if (!renderer || !latestCameraTexture) return null;
	const target = computeAspectFitSize(activeSession.latestCameraWidth, activeSession.latestCameraHeight, cameraFrame.captureSize);
	if (!cameraFrame.blit) cameraFrame.blit = new CameraBlitCapture(target);
	else cameraFrame.blit.resizeIfNeeded(target.width, target.height);
	return cameraFrame.blit.captureToRgba(renderer, latestCameraTexture);
}
/**
* Get the current raw AR pose from the latest XR frame.
* This is updated every frame and should be called when GPS arrives
* to get the AR pose at that moment.
*
* IMPORTANT: This returns the RAW pose from WebXR, NOT transformed
* by any alignment matrix. This is what we record to the store.
*
* @returns The latest AR pose, or null if no pose available yet
*/
function getCurrentArPose() {
	return activeSession.latestArPose;
}
/**
* Build XR session init options.
* Extracted as a pure function for testability.
*
* Exported for unit testing only (surface-reduction step 3, B-5 precedent):
* production code reaches this exclusively through {@link initAR}; the export
* exists so `webxr-session.test.ts` can pin the full session-negotiation
* matrix (isolation flags × session features) without mocking a renderer and
* `navigator.xr` for every combination. Not re-exported by the ar/ barrel.
*
* @param rootElement - The DOM element for DOM overlay
* @param isolationOptions - Crash-isolation diagnostic flags (DOM overlay,
*   depth-sensing, camera-access)
* @param sessionFeatures - Opt-in standard WebXR features that are independent
*   of crash isolation (`requestHitTest`, `requestDepthOcclusion`)
* @returns XRSessionInit options
* @throws Error if rootElement is null
*/
function buildSessionOptions(rootElement, isolationOptions = {}, sessionFeatures = {}) {
	if (!rootElement) throw new Error("App root element not found");
	const normalizedOptions = validateArCrashIsolationOptions(isolationOptions);
	const optionalFeatures = [];
	const sessionOptions = { requiredFeatures: ["local-floor"] };
	if (normalizedOptions.enableDomOverlay) {
		optionalFeatures.push("dom-overlay");
		sessionOptions.domOverlay = { root: rootElement };
	}
	if (normalizedOptions.enableDepthSensingFeature || sessionFeatures.requestDepthOcclusion) {
		optionalFeatures.push("depth-sensing");
		Object.assign(sessionOptions, { depthSensing: {
			usagePreference: ["cpu-optimized"],
			dataFormatPreference: ["luminance-alpha", "float32"]
		} });
	}
	if (normalizedOptions.enableCameraAccess) optionalFeatures.push("camera-access");
	if (sessionFeatures.requestHitTest) optionalFeatures.push("hit-test");
	if (optionalFeatures.length > 0) sessionOptions.optionalFeatures = optionalFeatures;
	return sessionOptions;
}
/**
* Check if WebXR immersive-ar is supported.
*
* Delegates to the timeout-guarded {@link probeImmersiveArSupport} — a
* wedged OS XR runtime can make the underlying `isSessionSupported`
* promise never settle (2026-07-24), and this check must degrade to
* `false` instead of hanging the caller's boot path.
*/
async function isWebXRSupported() {
	return probeImmersiveArSupport();
}
/**
* Initialize the AR session and Three.js renderer.
* @param container - DOM element to host the AR canvas and CSS3D overlay.
* @param isolationOptions - Crash-isolation diagnostic flags.
* @param sessionFeatures - Opt-in standard WebXR features (e.g.
*   `requestHitTest`) forwarded to the session negotiation.
* @param callbacks - Host callbacks for this session (see
*   {@link ArSessionCallbacks}); unpacked once into the session handle's
*   slots, which `resetWebXRState()` replaces wholesale at session end.
*/
async function initAR(container, isolationOptions = {}, sessionFeatures = {}, callbacks = {}) {
	if (!navigator.xr) throw new Error("WebXR not available");
	if (activeSession.sceneGraph.renderer || activeSession.xrSession) throw new Error("AR session already initialized — call endARSession() before initAR() again");
	activeSession = createArSessionHandle(validateArCrashIsolationOptions(isolationOptions), callbacks);
	const { sceneGraph } = activeSession;
	if (activeSession.crashIsolation.applyChromiumProjectionLayerWorkaround) applyChromiumProjectionLayerWorkaround();
	const renderer = new THREE.WebGLRenderer({
		antialias: true,
		alpha: true
	});
	sceneGraph.renderer = renderer;
	renderer.setPixelRatio(window.devicePixelRatio);
	renderer.setSize(window.innerWidth, window.innerHeight);
	renderer.xr.enabled = true;
	container.insertBefore(renderer.domElement, container.firstChild);
	if (activeSession.crashIsolation.enableCss3dRenderer) sceneGraph.css3d = createCss3dRendererManager(container, window.innerWidth, window.innerHeight);
	const hierarchy = createSceneHierarchy();
	sceneGraph.scene = hierarchy.scene;
	sceneGraph.arWorldGroup = hierarchy.arWorldGroup;
	sceneGraph.camera = hierarchy.camera;
	const sessionOptions = buildSessionOptions(container, activeSession.crashIsolation, sessionFeatures);
	const xrSession = await navigator.xr.requestSession("immersive-ar", sessionOptions);
	activeSession.xrSession = xrSession;
	xrSession.addEventListener("end", () => {
		handleSessionEnded();
	});
	await renderer.xr.setSession(xrSession);
	const trackingStore = activeSession.tracking.store;
	if (trackingStore) {
		const store = trackingStore;
		store.dispatch(resetTracking());
		activeSession.tracking.phaseUnsubscribe = subscribeToTrackingPhase(store);
		const referenceSpace = renderer.xr.getReferenceSpace();
		if (referenceSpace) referenceSpace.addEventListener("reset", (event) => {
			const transformData = extractResetTransformData(event);
			store.dispatch(originReset(transformData));
			log.warn("XR reference space reset detected", transformData ? "(transform available)" : "(no transform)");
		});
	}
	const onDepthSampleCaptured = activeSession.depth.onCaptured;
	if (onDepthSampleCaptured) {
		const depthCallbacks = {
			onSampleCaptured: onDepthSampleCaptured,
			getCurrentPose: getCurrentArPose,
			acquireRgbLookup: acquireDepthRgbLookup,
			onDepthUnavailable: activeSession.depth.onUnavailable ?? void 0
		};
		activeSession.depth.sampler = new DepthSampler(depthCallbacks);
	}
	const deliverCameraFrame = activeSession.cameraFrame.onFrame;
	if (deliverCameraFrame) activeSession.cameraFrame.source = new CameraFrameSource({
		capture: acquireCameraFrameRgba,
		onCapture: (image) => deliverCameraFrame(image)
	});
	renderer.setAnimationLoop(onXRFrame);
	log.info("AR session started");
}
/**
* Snapshot the current `DeviceOrientation` (with documented fallback
* defaults) for inclusion in `poseReceived` payloads.
*/
function snapshotDeviceOrientation() {
	const orientation = getLastDeviceOrientation();
	return {
		alpha: orientation?.alpha ?? 0,
		beta: orientation?.beta ?? 0,
		gamma: orientation?.gamma ?? 0,
		absolute: orientation?.absolute ?? false
	};
}
/**
* Wire the tracking-slice → host-callbacks translation. The subscriber
* runs synchronously inside each `dispatch`, so the host callbacks fire
* in the same order as a direct invocation would.
*
* Translation rules (locked in by tracking-slice tests; the host callbacks
* live on `activeSession.tracking`):
*   - `tracking → lost`: clear `latestArPose` (drops in-flight GPS events)
*     and call the host's `onLost`.
*   - `lost → tracking` with `lastRestartedPayload !== null` (Case 2):
*     call the host's `onRestarted(payload)` then dispatch
*     `clearLastRestartedPayload` so a subsequent loss cycle starts clean.
*   - `lost → tracking` with payload null (Case 1: seamless recovery):
*     call the host's `onRecovered`.
*   - `initializing → tracking`: no callback (initial acquisition is not
*     a restart — same behaviour as the manager).
*/
function subscribeToTrackingPhase(store) {
	let prev = "initializing";
	return store.subscribe(() => {
		const next = selectTrackingPhase(store.getState());
		if (next === prev) return;
		const previous = prev;
		prev = next;
		if (previous === "tracking" && next === "lost") {
			log.warn("Tracking lost");
			activeSession.latestArPose = null;
			activeSession.tracking.onLost?.();
			return;
		}
		if (previous === "lost" && next === "tracking") {
			const payload = selectLastRestartedPayload(store.getState());
			if (payload !== null) {
				log.info("Tracking restarted (origin reset)");
				activeSession.tracking.onRestarted?.(payload);
				store.dispatch(clearLastRestartedPayload());
			} else {
				log.info("Tracking recovered (same coordinate frame)");
				activeSession.tracking.onRecovered?.();
			}
		}
	});
}
/**
* Dispatch the per-frame `poseReceived` / `poseLost` action into the
* tracking slice. No-op when no store is bound (tracking wiring was not
* requested via initAR `callbacks.tracking`).
*/
function updateTrackingState(arPose) {
	const { store } = activeSession.tracking;
	if (!store) return;
	if (arPose) store.dispatch(poseReceived({
		pose: arPose,
		sensorOrientation: snapshotDeviceOrientation()
	}));
	else store.dispatch(poseLost());
}
/**
* Called each XR frame
*/
function onXRFrame(time, frame) {
	const { renderer, scene, camera } = activeSession.sceneGraph;
	if (!renderer || !scene || !camera || !frame) return;
	const referenceSpace = renderer.xr.getReferenceSpace();
	if (!referenceSpace) return;
	const pose = frame.getViewerPose(referenceSpace);
	const arPose = extractPoseFromViewer(pose);
	updateTrackingState(arPose);
	const dt = activeSession.lastFrameTime === 0 ? 0 : (time - activeSession.lastFrameTime) / 1e3;
	const elapsed = time / 1e3;
	activeSession.lastFrameTime = time;
	runFrameUpdates(dt, elapsed);
	const { xrSession } = activeSession;
	if (xrSession) runXrFrameUpdates({
		frame,
		referenceSpace,
		session: xrSession,
		dt,
		elapsed
	});
	if (arPose) activeSession.latestArPose = arPose;
	activeSession.latestCameraTexture = null;
	if (activeSession.crashIsolation.enableCameraTextureAcquisition) {
		const xrCamera = getXrCameraFromPose(pose);
		if (shouldLogCameraAccessDiagnostic(pose, activeSession.cameraAccessLoggedOnce, activeSession.imageCapture.manager !== null)) {
			activeSession.cameraAccessLoggedOnce = true;
			if (xrCamera) log.info("camera-access GRANTED — XRView.camera is available for blit capture");
			else log.warn("camera-access NOT GRANTED — XRView.camera is undefined. Image capture will fall back to canvas.toBlob() which may produce black/empty frames in WebXR.");
		}
		if (xrCamera) {
			const result = acquireCameraTexture(renderer, xrCamera);
			if (result) {
				activeSession.latestCameraTexture = result.texture;
				activeSession.latestCameraWidth = result.width;
				activeSession.latestCameraHeight = result.height;
			} else {
				activeSession.getCameraTextureNullCount++;
				if (activeSession.getCameraTextureNullCount <= GET_CAMERA_TEXTURE_LOG_THRESHOLD) log.warn(`getCameraTexture() returned null (occurrence ${activeSession.getCameraTextureNullCount}/${GET_CAMERA_TEXTURE_LOG_THRESHOLD}). camera-access is granted but Three.js did not provide a texture.`);
			}
		}
	}
	const { manager: imageManager } = activeSession.imageCapture;
	if (imageManager) imageManager.onFrame(time);
	const { sampler: depthSampler } = activeSession.depth;
	if (depthSampler) depthSampler.onFrame(time, () => getDepthInfoFromFrame(frame, pose));
	const { source: cameraFrameSource } = activeSession.cameraFrame;
	if (cameraFrameSource) cameraFrameSource.onFrame(time);
	const { onFrame } = activeSession;
	if (onFrame) try {
		onFrame();
	} catch (error) {
		log.error("Error in onFrame callback:", error);
	}
	renderer.render(scene, camera);
	const { css3d } = activeSession.sceneGraph;
	if (activeSession.crashIsolation.enableCss3dRenderer && css3d) css3d.render(scene, camera);
}
/**
* Extract depth information from an XR frame. Returns `null` if depth sensing is
* not available (no pose/view, no `getDepthInformation`, or the call throws).
*
* Exported so a consumer can feed the **live depth occluder** from a
* `registerXrFrameUpdate` callback — the callback has the live `frame` +
* `referenceSpace`, computes `pose = frame.getViewerPose(referenceSpace)`, and
* passes both here to obtain the per-frame {@link DepthInfo} (with the widened
* `data` / `rawValueToMeters` / `normDepthBufferFromNormView` / `projectionMatrix`
* the occluder needs). The same wrapped depth the sparse grid sampler consumes.
*/
function getDepthInfoFromFrame(frame, pose) {
	const view = pose?.views[0];
	if (!view) return null;
	const xrFrame = frame;
	if (typeof xrFrame.getDepthInformation !== "function") return null;
	try {
		const result = xrFrame.getDepthInformation(view);
		if (!result) return null;
		return wrapXRDepthInfo(result, view.projectionMatrix);
	} catch {
		return null;
	}
}
/**
* Get the current Three.js scene (for adding objects like map)
*/
function getScene() {
	return activeSession.sceneGraph.scene;
}
/**
* Get the AR world group (for adding AR-tracked content)
* Content added here will be transformed by the alignment matrix.
*/
function getArWorldGroup() {
	return activeSession.sceneGraph.arWorldGroup;
}
/**
* Get the current camera
*/
function getCamera() {
	return activeSession.sceneGraph.camera;
}
/**
* Apply an alignment matrix to the AR world group.
*
* The alignment matrix maps odometry positions in NUE space
* to GPS world space (also NUE). It is applied directly to arWorldGroup
* whose local space is NUE.
*
* The WebXR→NUE basis change lives permanently in basisChangeNode (a
* child of arWorldGroup), so the full camera world transform is:
*
*   camera_world = arWorldGroup × basisChangeNode × arpose × camera_local
*               = alignment × WEBXR_TO_NUE × arpose × camera_local
*
* This is mathematically identical to the previous composition
* (alignment × WEBXR_TO_NUE applied directly to arWorldGroup.matrix),
* but arWorldGroup's local space is now NUE: objects placed as children
* of arWorldGroup use NUE coordinates directly ([1,0,0]=North, [0,0,1]=East).
*
* Replay note: arpose still lives in WebXR space (below basisChangeNode), so
* the library's nueToWebXR() is still required when setting arpose.position.
*
* @param matrix - 16-element column-major matrix (gl-matrix mat4 format)
*/
function applyAlignmentMatrix(matrix) {
	const { arWorldGroup } = activeSession.sceneGraph;
	if (!arWorldGroup) {
		log.warn("Cannot apply alignment - arWorldGroup not initialized");
		return;
	}
	if (matrix.length !== 16) {
		log.error("Invalid alignment matrix - expected 16 elements");
		return;
	}
	arWorldGroup.matrix.fromArray(matrix);
	arWorldGroup.matrixAutoUpdate = false;
	arWorldGroup.updateMatrixWorld(true);
}
/**
* The ONE teardown for a session that has ended, shared by both trigger
* paths via the XRSession 'end' listener. Before F3 the system-initiated
* path (back gesture) only nulled `xrSession`/`latestArPose`, leaving the
* renderer compositing the 3D scene over a black camera background and the
* host app never notified — the "haunted scene" from
* docs/2026-02-15-lifecycle-orphans.md §1.
*/
function handleSessionEnded() {
	log.info("Session ended");
	const callback = activeSession.onSessionEnd;
	const requestedByApp = activeSession.endRequestedByApp;
	activeSession.tracking.store?.dispatch(resetTracking());
	resetWebXRState();
	if (callback) try {
		callback({ requestedByApp });
	} catch (err) {
		log.error("Session-end callback threw:", err);
	}
}
/**
* End the current XR session and clean up all resources.
*
* Stops the animation loop, ends the XR session, then delegates the full
* teardown to {@link resetWebXRState} so every session reference is
* cleared (renderer/scene/camera, image-capture, depth, the tracking-phase
* subscription, the frame-update registry, diagnostics, blit resources).
* This is the production cleanup path — call it when the AR experience is
* finished.
*/
async function endARSession() {
	activeSession.sceneGraph.renderer?.setAnimationLoop(null);
	try {
		const { xrSession } = activeSession;
		if (xrSession) {
			activeSession.endRequestedByApp = true;
			await xrSession.end();
		}
	} finally {
		resetWebXRState();
	}
}
/**
* Start capturing images during recording.
* Requires the `imageCapture` callbacks group to have been passed to initAR.
*
* @param config - Optional capture configuration. Accepts the whole user
*   image-options section (`intervalMs`, `quality`, `resolutionDivisor`; any
*   extra keys such as `enabled` are ignored). Passing the section as one
*   object means a newly-added option flows through without editing this seam
*   — see `2026-06-12-1130-payload-rebuild-field-drop-audit.md` (F3).
*/
function startImageCapture(config) {
	const { renderer } = activeSession.sceneGraph;
	if (!renderer) {
		log.warn("Cannot start image capture - renderer not initialized");
		return;
	}
	const { imageCapture } = activeSession;
	const onCaptured = imageCapture.onCaptured;
	const getRotation = imageCapture.getScreenRotation;
	if (!onCaptured || !getRotation) {
		log.warn("Cannot start image capture - callbacks not set");
		return;
	}
	if (imageCapture.manager || imageCapture.blit) {
		log.warn("Image capture already running - stopping previous session");
		stopImageCapture();
	}
	const callbacks = {
		getCurrentPose: getCurrentArPose,
		getScreenRotation: getRotation,
		onCaptured,
		onCaptureFailed: imageCapture.onFailed ?? void 0,
		onSuspiciousImage: imageCapture.onSuspicious ?? void 0,
		analyzeFrame: imageCapture.qualityAnalyzer ?? void 0
	};
	const mergedConfig = {
		...DEFAULT_CAPTURE_CONFIG,
		...config
	};
	imageCapture.blit = new CameraBlitCapture();
	const currentRenderer = renderer;
	const divisor = mergedConfig.resolutionDivisor;
	callbacks.captureFrame = async (quality) => {
		const bc = activeSession.imageCapture.blit;
		const { latestCameraTexture, latestCameraWidth, latestCameraHeight } = activeSession;
		if (!bc || !latestCameraTexture) {
			const canvas = currentRenderer.domElement;
			return new Promise((resolve) => {
				canvas.toBlob((blob) => resolve(blob ? {
					blob,
					width: canvas.width,
					height: canvas.height
				} : null), "image/jpeg", quality);
			});
		}
		if (latestCameraWidth > 0 && latestCameraHeight > 0) {
			const target = computeCaptureSize(latestCameraWidth, latestCameraHeight, divisor);
			bc.resizeIfNeeded(target.width, target.height);
		}
		const blob = await bc.captureToBlob(currentRenderer, latestCameraTexture, quality);
		if (!blob) return null;
		return {
			blob,
			width: bc.getWidth(),
			height: bc.getHeight()
		};
	};
	log.info(`Blit capture pipeline initialized (resolutionDivisor=${divisor})`);
	imageCapture.manager = new ImageCaptureManager(renderer.domElement, callbacks, mergedConfig);
	imageCapture.manager.start();
	log.info("Image capture started");
}
/**
* Stop capturing images.
*/
function stopImageCapture() {
	const { imageCapture } = activeSession;
	if (imageCapture.manager) {
		imageCapture.manager.stop();
		log.info(`Image capture stopped (${imageCapture.manager.getFrameCount()} frames captured)`);
		imageCapture.manager = null;
	}
	cleanupBlitResources();
}
/**
* Get the current image capture frame count.
*/
function getImageCaptureFrameCount() {
	return activeSession.imageCapture.manager?.getFrameCount() ?? 0;
}
/**
* Re-point the tracking pipeline at a NEW store mid-session.
*
* This is the ONE mutation that deliberately survived the fold of the
* pre-init setters into initAR's `callbacks` (surface-reduction step 1):
* it is a genuine RUNTIME need, not init-time wiring — the recorder swaps
* its Redux store for every recording started inside a single AR session,
* and without re-pointing, every per-frame `poseReceived` dispatch would
* keep flowing into the orphaned previous store, pinning the new store's
* `tracking.phase` at 'initializing' (Finding #1, 2026-05-23 user
* feedback). Everything else about a session's callbacks stays fixed from
* `initAR` until `resetWebXRState()`.
*
* Matches the old `setTrackingStore` semantics exactly: an active phase
* subscription to the previous store is torn down before swapping (the
* phase subscription itself is only (re)established inside `initAR`).
* No-op pipeline when tracking was not wired at init (the host callbacks
* stay whatever `initAR` set).
*
* @param store — any store satisfying {@link TrackingSubscribableStore}.
*/
function rebindTrackingStore(store) {
	const { tracking } = activeSession;
	if (tracking.phaseUnsubscribe) {
		tracking.phaseUnsubscribe();
		tracking.phaseUnsubscribe = null;
	}
	tracking.store = store;
}
/**
* Start depth sampling during recording.
* Requires the `depth` callbacks group to have been passed to initAR
* (the sampler is created there).
*
* @param config - optional sampler overrides (typically the user's
*   `depth.intervalMs`/`depth.gridSize` recording options); applied via
*   `DepthSampler.updateConfig` before sampling starts. Without this the
*   sampler's own defaults apply — the settings knobs were dead before
*   this parameter existed (occupancy-grid port plan, Iter 6).
*/
function startDepthCapture(config) {
	const { sampler } = activeSession.depth;
	if (!sampler) {
		log.warn("Cannot start depth capture - sampler not initialized");
		return;
	}
	if (config) sampler.updateConfig(config);
	sampler.start();
	log.info(`Depth capture started (interval: ${sampler.getConfig().intervalMs}ms, grid: ${sampler.getConfig().gridSize}×${sampler.getConfig().gridSize})`);
}
/**
* Stop depth sampling.
*/
function stopDepthCapture() {
	const { sampler } = activeSession.depth;
	if (sampler) {
		const count = sampler.getSampleCount();
		sampler.stop();
		log.info(`Depth capture stopped (${count} samples captured)`);
	}
}
/**
* Get the current depth sample count.
*/
function getDepthSampleCount() {
	return activeSession.depth.sampler?.getSampleCount() ?? 0;
}
/**
* Start camera frame capture during an AR session. Requires the
* `cameraFrame` callbacks group to have been passed to initAR (the source is
* created there); the callback receives **top-left-origin** RGBA images (no
* JPEG round-trip) at the throttled detection cadence — feed them straight to
* a `BarcodeDetector` / OpenCV front-end (B2). No-op if the source was not
* initialized (group not passed to initAR).
*
* The source is the single cadence owner: drive your detection scheduler from
* the delivered frames with its own `minIntervalMs: 0` (Option A).
*
* @param config - optional cadence / blit-resolution overrides.
*/
function startCameraFrameCapture(config) {
	const { cameraFrame } = activeSession;
	if (!cameraFrame.source) {
		log.warn("Cannot start camera frame capture - frame source not initialized");
		return;
	}
	if (typeof config?.captureSize === "number" && Number.isFinite(config.captureSize) && config.captureSize > 0) cameraFrame.captureSize = Math.floor(config.captureSize);
	if (config?.intervalMs !== void 0) cameraFrame.source.updateConfig({ intervalMs: config.intervalMs });
	cameraFrame.source.start();
	log.info(`Camera frame capture started (interval: ${cameraFrame.source.getConfig().intervalMs}ms, long edge ${cameraFrame.captureSize}px, aspect-preserved)`);
}
/**
* Stop camera frame capture. Safe to call when not running.
*/
function stopCameraFrameCapture() {
	const { source } = activeSession.cameraFrame;
	if (source) {
		const count = source.getFrameCount();
		source.stop();
		log.info(`Camera frame capture stopped (${count} frames captured)`);
	}
}
/**
* Get the number of camera frames captured since the last
* `startCameraFrameCapture`.
*/
function getCameraFrameCount() {
	return activeSession.cameraFrame.source?.getFrameCount() ?? 0;
}
//#endregion
export { startDepthCapture as C, stopImageCapture as D, stopDepthCapture as E, CameraFrameSource as O, startCameraFrameCapture as S, stopCameraFrameCapture as T, isWebXRSupported as _, extractPoseFromViewer as a, resetWebXRState as b, getCamera as c, getDepthInfoFromFrame as d, getDepthSampleCount as f, initAR as g, getXrCameraFromPose as h, endARSession as i, getCameraFrameCount as l, getScene as m, applyAlignmentMatrix as n, extractResetTransformData as o, getImageCaptureFrameCount as p, buildSessionOptions as r, getArWorldGroup as s, DEFAULT_CAMERA_FRAME_CAPTURE_SIZE as t, getCurrentArPose as u, isXRCameraLike as v, startImageCapture as w, shouldLogCameraAccessDiagnostic as x, rebindTrackingStore as y };
